// Chamadas à API do servidor. O token de login fica guardado no navegador.
import type { AuthResponse, CharacterSummary, CreateCharacterRequest } from '../../shared/src/protocol';
import type { HuntInfo } from '../../shared/src/game/world';

const TOKEN_KEY = 'noxara.token';
const EMAIL_KEY = 'noxara.email';

function storageGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function storageSet(key: string, value: string | null) {
  try { value === null ? localStorage.removeItem(key) : localStorage.setItem(key, value); } catch { /* modo privado */ }
}

export const session = {
  get token() { return storageGet(TOKEN_KEY); },
  get email() { return storageGet(EMAIL_KEY); },
  save(r: AuthResponse) { storageSet(TOKEN_KEY, r.token); storageSet(EMAIL_KEY, r.email); },
  clear() { storageSet(TOKEN_KEY, null); },
};

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch('/api' + path, {
      method,
      headers: { 'content-type': 'application/json', ...(session.token ? { authorization: `Bearer ${session.token}` } : {}) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'Sem conexão com o servidor.');
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, (data as any).error ?? 'Erro inesperado.');
  return data as T;
}

export const api = {
  register: (email: string, password: string, adult: boolean) => request<AuthResponse>('POST', '/register', { email, password, adult }),
  login: (email: string, password: string) => request<AuthResponse>('POST', '/login', { email, password }),
  config: () => request<{ googleClientId: string | null }>('GET', '/config'),
  googleLogin: (credential: string, adult: boolean) => request<AuthResponse>('POST', '/login/google', { credential, adult }),
  account: () => request<{ email: string; hasPassword: boolean; google: boolean; createdAt: string }>('GET', '/account'),
  changePassword: (current: string, password: string) => request<{ ok: true }>('POST', '/account/password', { current, password }),
  logout: () => request<{ ok: true }>('POST', '/logout', {}),
  characters: () => request<CharacterSummary[]>('GET', '/characters'),
  createCharacter: (c: CreateCharacterRequest) => request<CharacterSummary>('POST', '/characters', c),
  deleteCharacter: (id: number, name: string, password: string) => request<{ ok: true }>('POST', `/characters/${id}/delete`, { name, password }),
  hunts: () => request<Array<HuntInfo & { online: number }>>('GET', '/hunts'),
};
